import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiCheck, FiUsers, FiTrendingUp, FiStar, FiPlay, FiInfo, FiChevronDown, FiChevronUp } = FiIcons;

function PunchyLandingPage() {
  const [stickyVisible, setStickyVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('getting-to-know-you');
  const [transcriptVisible, setTranscriptVisible] = useState(false);
  const currentYear = new Date().getFullYear();
  const checkoutUrl = "https://courses.itskaivalya.com/checkouts/checkout-page/?add-to-cart=67&quantity=1&aero-coupons=punchy-416";

  useEffect(() => {
    const handleScroll = () => {
      setStickyVisible(window.scrollY > 800);
      
      // Update active section based on scroll position
      const sections = [
        'getting-to-know-you', 'basics', 'app-concept', 'validating-the-concept',
        'wireframing', 'design-briefs', 'development', 'marketing-content',
        'automation', 'final-thoughts', 'bonus'
      ];
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCTAClick = () => {
    window.open(checkoutUrl, '_blank');
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      element.focus();
    }
  };

  const curriculumSections = [
    { id: 'getting-to-know-you', title: 'Getting to know you', description: 'Introduction and orientation.' },
    { id: 'basics', title: 'Basics', description: 'Core foundations of AI-assisted full-stack development.' },
    { id: 'app-concept', title: 'App Concept', description: 'Understanding and scoping app ideas.' },
    { id: 'validating-the-concept', title: 'Validating the Concept', description: 'Using AI and research methods to validate feasibility and demand.' },
    { id: 'wireframing', title: 'Wireframing', description: 'Designing the flow and structure before development.' },
    { id: 'design-briefs', title: 'Design Briefs', description: 'Translating wireframes into actionable design specs.' },
    { id: 'development', title: 'Development', description: 'Hands-on building with AI support for frontend, backend, auth, and APIs.' },
    { id: 'marketing-content', title: 'Marketing Content', description: 'Creating the launch assets and positioning around your app.' },
    { id: 'automation', title: 'Automation', description: 'Using AI tools to streamline workflows and app processes.' },
    { id: 'final-thoughts', title: 'Final Thoughts', description: 'Wrap-up, polishing, and preparing your portfolio.' },
    { id: 'bonus', title: 'Bonus', description: 'Extra tips, case studies, and advanced techniques.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Skip to content link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-black text-white px-4 py-2 rounded-lg z-50 focus:outline-none focus:ring-4 focus:ring-gray-300"
      >
        Skip to main content
      </a>

      {/* Hero Section */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold text-black mb-8 leading-tight"
          >
            Build Real Apps. Get Real Results.
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-600 mb-8"
          >
            <p className="font-bold text-black mb-4">No fluff. No theory. No excuses.</p>
            
            <div className="text-left max-w-2xl mx-auto space-y-3">
              <p>You want to:</p>
              <ul className="space-y-2 text-lg">
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  Ship apps that actually work.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  Have proof for interviews, clients, and promotions.
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gray-600 mt-1">•</span>
                  Stop wasting nights on tutorials and hallucinated AI garbage.
                </li>
              </ul>
            </div>
            
            <p className="font-bold text-black text-xl mt-6">This is how you do it.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8"
          >
            <p className="text-3xl font-bold text-black mb-2">Monthly tuition: ₹416/mo</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-3 bg-black hover:bg-gray-800 focus:bg-gray-800 text-white font-bold px-8 py-4 rounded-lg text-xl transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-300 min-h-[44px]"
            >
              Join — ship your first app fast
              <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
            </button>
          </motion.div>
        </div>
      </section>

      <main id="main-content">
        {/* Primary Curriculum Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-4">
                What you'll learn inside (Modules Overview)
              </h1>
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="bg-black text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                  KD
                </div>
                <p className="text-lg text-gray-600">By Kaivalya Deshpande</p>
              </div>
              <p className="text-gray-600">Categories: ai</p>
            </div>

            <div className="grid lg:grid-cols-4 gap-8">
              {/* Curriculum Navigation */}
              <div className="lg:col-span-1">
                <nav aria-label="Course curriculum" className="sticky top-6">
                  <h3 className="text-lg font-bold text-black mb-4">Course Curriculum</h3>
                  <ol className="space-y-2">
                    {curriculumSections.map((section, index) => (
                      <li key={section.id}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                            activeSection === section.id
                              ? 'bg-black text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                          aria-current={activeSection === section.id ? 'true' : 'false'}
                          aria-controls={section.id}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              {index + 1}. {section.title}
                            </span>
                            {section.hasInfo && (
                              <SafeIcon icon={FiInfo} className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>

              {/* Course Content */}
              <div className="lg:col-span-3">
                {/* YouTube Course Trailer */}
                <div 
                  className="mb-8" 
                  role="region" 
                  aria-label="Course preview video"
                >
                  <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                    <iframe 
                      src="https://www.youtube.com/embed/JZXaqpxG9ow?rel=0&modestbranding=1" 
                      title="Course preview and trailer" 
                      width="100%" 
                      height="480" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  
                  {/* Off-screen description for screen readers */}
                  <p id="trailer-desc" className="sr-only">
                    Course preview trailer showing the AI development program structure, sample projects, and learning outcomes for building full-stack applications.
                  </p>
                  
                  {/* Transcript/Summary */}
                  <div className="mt-4">
                    <button
                      onClick={() => setTranscriptVisible(!transcriptVisible)}
                      className="flex items-center gap-2 text-gray-700 hover:text-black font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 rounded px-2 py-1"
                      aria-expanded={transcriptVisible}
                      aria-controls="video-transcript"
                    >
                      <SafeIcon icon={transcriptVisible ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
                      {transcriptVisible ? 'Hide' : 'Show'} Transcript
                    </button>
                    
                    {transcriptVisible && (
                      <div 
                        id="video-transcript" 
                        className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <h4 className="font-semibold text-black mb-2">Video Transcript:</h4>
                        <p className="text-gray-700 leading-relaxed">
                          Course preview: A short trailer showing what you will build in this program — production-ready apps, live calls, and outcomes. Watch to see sample projects and the practical, results-first approach we use.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Fallback for when YouTube is blocked */}
                  <noscript>
                    <div className="bg-gray-100 rounded-lg p-8 text-center">
                      <p className="text-gray-700 mb-4">Video preview not available. Watch the course trailer:</p>
                      <a 
                        href="https://www.youtube.com/watch?v=JZXaqpxG9ow" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        <SafeIcon icon={FiPlay} className="w-5 h-5" />
                        Watch on YouTube
                      </a>
                    </div>
                  </noscript>
                </div>

                {/* Curriculum Sections Content */}
                <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
                  <div className="prose prose-gray max-w-none">
                    {curriculumSections.map((section) => (
                      <div 
                        key={section.id} 
                        id={section.id} 
                        className="mb-12 scroll-mt-6" 
                        tabIndex="-1"
                        role="region"
                        aria-labelledby={`${section.id}-heading`}
                      >
                        <h3 
                          id={`${section.id}-heading`}
                          className="text-xl font-bold text-black mb-4 border-b border-gray-200 pb-2"
                        >
                          Module {curriculumSections.findIndex(s => s.id === section.id) + 1}: {section.title}
                        </h3>
                        <div className="text-gray-700">
                          <p className="mb-4">{section.description}</p>
                          <p>This program includes weekly live sessions with Kaivalya for debugging and Q&A, plus recorded sessions for self-paced catch-up.</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">The Problem</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p>Most people spend months "learning" and have nothing to show.</p>
              <div className="space-y-3">
                <p>YouTube tutorials = broken.</p>
                <p>AI code = lies.</p>
                <p>Result = 0 apps, 0 clients, 0 growth.</p>
              </div>
              <p className="text-xl font-bold text-black text-center">
                That's why most stay stuck.
              </p>
            </div>
          </div>
        </section>

        {/* The Solution */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">The Solution</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p className="text-xl font-bold text-black">We fix it.</p>
              <p>End-to-end builds. Live, on screen. Kushl.app. Annapurna Foods. Yatric platform. Solar Calculator. Done.</p>
              <p>Weekly calls. You bring your error. We fix it.</p>
              <p className="text-xl font-bold text-black">
                Proof in hand. Real apps. Live links. No BS.
              </p>
            </div>
          </div>
        </section>

        {/* What You'll Do */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">What You'll Do</h2>
            
            <div className="space-y-4 text-lg text-gray-700">
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>UI fast:</strong> V0. Screens in minutes.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Backend/auth:</strong> Supabase. Secure, scalable.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Payments:</strong> Razorpay. Orders, carts, dashboards.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Marketplaces:</strong> roles, maps, approvals. Yatric-level logic.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Dashboards/tools:</strong> ROI calculators, business KPIs.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>AI workflows:</strong> custom GPTs, automation, research systems.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Capstone:</strong> Ship a Progressive Web App. Share the live link.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Timeline (Punchy framing)</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Week 2:</strong> Demo live.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Week 4:</strong> Ordering app/marketplace deployed.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Week 6:</strong> Dashboards + workflows running.</p>
              </div>
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiCheck} className="text-gray-800 mt-1 w-5 h-5" />
                <p><strong>Month 2–3:</strong> First freelance client or promotion.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Who this is for */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Who this is for</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-4">Students</h3>
                <p className="text-gray-700">Who need portfolios with proof.</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-4">Interns/Juniors</h3>
                <p className="text-gray-700">Who want to stop being invisible.</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiStar} className="w-12 h-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-black mb-4">Freelancers</h3>
                <p className="text-gray-700">Who must deliver fast and charge more.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Objections */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Objections (answered fast)</h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"I can't code."</h3>
                <p className="text-gray-700">→ Doesn't matter. We scaffold with AI.</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"AI lies."</h3>
                <p className="text-gray-700">→ True. That's why we validate live.</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"What if I get stuck?"</h3>
                <p className="text-gray-700">→ Weekly calls. Done.</p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="font-bold text-xl mb-2 text-black">"What if it doesn't work?"</h3>
                <p className="text-gray-700">→ 60-day refund. No excuses.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Proof from our students */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Proof from our students</h2>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-3xl font-bold text-black mb-2">533+</p>
                <p className="text-lg text-gray-700">learners onboard already.</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-black mb-2">39–62%</p>
                <p className="text-lg text-gray-700">average salary bumps reported.</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-black mb-2">2–8 weeks</p>
                <p className="text-lg text-gray-700">freelancers landing clients.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12">Stop learning. Start shipping.</h2>
            
            <div className="space-y-6 text-xl text-gray-700">
              <p>No more broken tutorials.</p>
              <p>No more AI lies.</p>
              <p>No more empty portfolios.</p>
              <p className="font-bold text-black">Just real apps. Real results. Real fast.</p>
            </div>

            <div className="mt-12">
              <button
                onClick={handleCTAClick}
                className="inline-flex items-center gap-3 bg-black hover:bg-gray-800 focus:bg-gray-800 text-white font-bold px-8 py-4 rounded-lg text-xl transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-300 min-h-[44px]"
              >
                Join — ship your first app fast
                <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
              </button>
            </div>
          </div>
        </section>

        {/* Instructor Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Meet Your Instructor: Kaivalya</h2>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://i.ibb.co/DHSRjPSv/kaivalya-profile-photo.jpg" 
                  alt="Kaivalya - AI Development Instructor"
                  className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-6">
                <p className="text-lg text-gray-700">
                  I've built and shipped dozens of AI-powered applications, from marketplaces handling thousands of users to business tools generating real revenue.
                </p>
                <p className="text-lg text-gray-700">
                  But more importantly, I've been where you are: stuck in tutorial hell, copy-pasting code that doesn't work, wondering if I'd ever build anything real.
                </p>
                <p className="text-lg text-gray-700">
                  That's why I created this program. Not to show off what I know, but to give you the exact system I wish I had when I started.
                </p>
                <p className="text-lg font-semibold text-black">
                  My promise: I'll be there every week, debugging your code, answering your questions, and making sure you actually ship real projects.
                </p>
                
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-black">56+</p>
                    <p className="text-sm text-gray-600">Apps Built & Deployed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-black">533+</p>
                    <p className="text-sm text-gray-600">Students Guided</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-black">3.4</p>
                    <p className="text-sm text-gray-600">Years Teaching AI Development</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Meet the Maitreya Labs Team</h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <img 
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/maitreya%20labs%20team%2001.jpg-CYzk0EHjNYGm0lsvWJXs0UcNAluuHS.jpeg" 
                alt="Maitreya Labs Team Member 1"
                className="w-full rounded-lg shadow-lg"
              />
              <img 
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/maitreya%20labs%20charty%20and%20ngo%20sessions.jpg-gOY6rfvY4MYCc5Wwcxq8Q4sS30vBBv.jpeg" 
                alt="Maitreya Labs Charity and NGO Sessions"
                className="w-full rounded-lg shadow-lg"
              />
              <img 
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Team%20Maitreya%20Labs%20team%202%20%281%29.png-nLZY55sOGLxWPnzzzhLt4Xo4NzhgH0.jpeg" 
                alt="Maitreya Labs Team Member 2"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            
            <p className="text-lg text-gray-700 text-center">
              From technical workshops to community outreach, we're committed to building not just better developers, but a better tech community that uses AI for positive impact.
            </p>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Real Results: What Clients Say</h2>
            
            <div className="max-w-3xl mx-auto mb-8">
              <div className="wistia_responsive_padding" style={{padding:'177.78% 0 0 0', position:'relative'}}>
                <div className="wistia_responsive_wrapper" style={{height:'100%', left:0, position:'absolute', top:0, width:'100%'}}>
                  <iframe 
                    src="https://fast.wistia.net/embed/iframe/pjhz34kw16?web_component=true&seo=true"
                    title="Nihal Testimonial Video"
                    allow="autoplay; fullscreen"
                    allowTransparency="true"
                    frameBorder="0"
                    scrolling="no"
                    className="wistia_embed"
                    name="wistia_embed"
                    width="100%"
                    height="100%">
                  </iframe>
                </div>
              </div>
            </div>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p>
                Using the same AI development techniques taught in this course, we've built complete applications, marketing funnels, and business solutions for clients across various industries.
              </p>
              <p>
                The result? Exceptional client satisfaction and testimonials like this one from Nihal.
              </p>
              <p className="font-semibold">
                Behind the scenes: The application and marketing systems that earned Nihal's praise were built entirely using AI-assisted development—the same methods you'll master in this course.
              </p>
            </div>
          </div>
        </section>

        {/* Student Success Stories */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-12 text-center">Student Success Stories</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg text-center border border-gray-200 shadow-sm">
                <p className="text-2xl font-bold text-black mb-2">"₹45,000 in first month"</p>
                <p className="text-gray-700">→ Landed 3 restaurant clients.</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center border border-gray-200 shadow-sm">
                <p className="text-2xl font-bold text-black mb-2">"Recurring client pays ₹20,000/month"</p>
                <p className="text-gray-700">→ Built an e-commerce store with ongoing revenue.</p>
              </div>
              <div className="bg-white p-6 rounded-lg text-center border border-gray-200 shadow-sm">
                <p className="text-2xl font-bold text-black mb-2">"Quit my job after 3 months"</p>
                <p className="text-gray-700">→ Now earning 2x corporate salary as a freelance AI developer.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-400">© {currentYear} ItsKaivalya. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky CTA Bar */}
      {stickyVisible && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-40">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <p className="font-bold text-black">Monthly tuition: ₹416/mo</p>
            </div>
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 focus:bg-gray-800 text-white font-bold px-6 py-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-300 min-h-[44px]"
            >
              Join now
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Wistia Script */}
      <script src="https://fast.wistia.net/player.js" async></script>
    </div>
  );
}

export default PunchyLandingPage;